import { Colorways } from 'types/constants';
import { ReactNode } from 'react';
import { BLOCKS, Document, INLINES, Node } from '@contentful/rich-text-types';
import { CustomQueryStringParameterItems } from 'libs/contentful/types';

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

// Contentful serializes an authored-but-empty field as one empty paragraph, so these wrappers
// are transparent; any other node (embedded asset/entry, list, table, hr) counts as content.
const TRANSPARENT_NODE_TYPES: string[] = [BLOCKS.DOCUMENT, BLOCKS.PARAGRAPH];

const nodeHasContent = (node: Node): boolean => {
  if ('value' in node && typeof node.value === 'string') {
    return node.value.trim().length > 0;
  }
  if ('content' in node && Array.isArray(node.content)) {
    if (!TRANSPARENT_NODE_TYPES.includes(node.nodeType)) return true;
    return node.content.some(nodeHasContent);
  }
  return true;
};

export const richTextHasContent = (document?: Document | null): boolean =>
  Array.isArray(document?.content) && document.content.some(nodeHasContent);

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const getIdFromText = (text: string) => {
  if (!text) return '';

  text = text.toLowerCase().replace(/[^a-z0-9-]/g, '-');

  return encodeURIComponent(text);
};

export const getTextFromChildren = (children: ReactNode): string => {
  if (typeof children === 'string' || typeof children === 'number') {
    return children.toString();
  }

  if (Array.isArray(children)) {
    return children.map(getTextFromChildren).join('');
  }

  if (typeof children === 'object' && children && 'props' in children) {
    return getTextFromChildren((children as { props: { children: ReactNode } }).props.children);
  }

  return '';
};

export const getCleanDocument = (document: Document | object): Document | object => {
  if (document && typeof document === 'object' && 'json' in document) {
    return document.json as Document;
  }

  return document;
};

// Inline embedded entries live outside the document tree: a node only carries a target
// sys.id, while the entry's data sits in `links.entries.inline`. This map lets the text
// extractor resolve that reference. Keyed by sys.id; values stay as raw records since the
// inline-entry union is wide and we only read a couple of fields per type.
type InlineEntryMap = Map<string, Record<string, unknown>>;

const BLOCK_CONTAINER_NODE_TYPES: ReadonlySet<string> = new Set([
  BLOCKS.DOCUMENT,
  BLOCKS.QUOTE,
  BLOCKS.UL_LIST,
  BLOCKS.OL_LIST,
  BLOCKS.LIST_ITEM,
  BLOCKS.TABLE,
  BLOCKS.TABLE_ROW,
  BLOCKS.TABLE_CELL,
  BLOCKS.TABLE_HEADER_CELL,
]);

const getEmbeddedInlineTargetId = (node: Record<string, unknown>): string | undefined => {
  const { data } = node;
  if (!isRecord(data) || !isRecord(data.target) || !isRecord(data.target.sys)) return undefined;
  const { id } = data.target.sys;
  return typeof id === 'string' ? id : undefined;
};

const buildInlineEntryMap = (document: Record<string, unknown>): InlineEntryMap => {
  const map: InlineEntryMap = new Map();
  const { links } = document;
  if (!isRecord(links) || !isRecord(links.entries) || !Array.isArray(links.entries.inline)) return map;

  for (const entry of links.entries.inline) {
    if (isRecord(entry) && isRecord(entry.sys) && typeof entry.sys.id === 'string') {
      map.set(entry.sys.id, entry);
    }
  }

  return map;
};

// Plain-text value of a resolved inline entry. Shortcodes hold reusable numbers (ATM counts,
// APY, cash-back %) in a nested rich-text field, so recurse into it; other inline types
// (disclosures, modals, line breaks) contribute no answer text.
const getInlineEntryText = (entry: Record<string, unknown>): string => {
  if (entry.__typename === 'Shortcode' && isRecord(entry.shortCodeValue)) {
    return getRichTextValue(entry.shortCodeValue);
  }

  return '';
};

export const extractTextFromNode = (node: unknown, inlineEntries?: InlineEntryMap): string => {
  if (!isRecord(node)) {
    return '';
  }

  // Resolve inline embedded entries against the links map so their text (e.g. a Shortcode's
  // number) is included. Without the map the node has no `value`/`content` and silently
  // yields '', dropping the number and leaving gaps like "more than  fee-free ATMs".
  if (node.nodeType === INLINES.EMBEDDED_ENTRY && inlineEntries) {
    const targetId = getEmbeddedInlineTargetId(node);
    const entry = targetId ? inlineEntries.get(targetId) : undefined;
    return entry ? getInlineEntryText(entry) : '';
  }

  // If the node has a value property, it's a text node
  if (typeof node.value === 'string') {
    return node.value;
  }

  if (Array.isArray(node.content)) {
    const separator = typeof node.nodeType === 'string' && BLOCK_CONTAINER_NODE_TYPES.has(node.nodeType) ? ' ' : '';
    return node.content.map(child => extractTextFromNode(child, inlineEntries)).join(separator);
  }

  return '';
};

export const getRichTextValue = (document: Document | object): string => {
  if (isRecord(document) && 'json' in document) {
    return extractTextFromNode(document.json, buildInlineEntryMap(document));
  }

  if (typeof document === 'string') {
    return document;
  }

  return '';
};

export const getColorwayName = (colorway: { name: Colorways } | Colorways | null | undefined): Colorways => {
  if (!colorway) {
    return Colorways.Primary;
  }

  if (typeof colorway === 'object' && 'name' in colorway) {
    return colorway.name;
  }

  return colorway;
};

export const pascalToSnake = (str: string) => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1_$2') // Insert an underscore before uppercase letters
    .toLowerCase(); // Convert the whole string to lowercase
};

export const pascalToCamel = (str: string) => {
  return str.charAt(0).toLowerCase() + str.slice(1);
};

export const titleToCamel = (str: string) => {
  const stripped = str.replace(/\s+/g, '');
  return stripped.charAt(0).toLowerCase() + stripped.slice(1);
};

export const toSnakeCase = (str: string) => {
  return str.replace(/\W+/g, ' ').trim().toLowerCase().replace(/\s/g, '_');
};

export const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const formatDate = (str: string) => {
  const opts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  };
  const parsedDate = Date.parse(str);
  return new Date(parsedDate).toLocaleDateString('en-US', opts);
};

/** Reads key from an item that may be CustomQueryStringParameterItems or REST-adapted shape. */
function getParamKey(item: Record<string, unknown>): string | undefined {
  const key = item.key ?? item.paramKey;
  return typeof key === 'string' ? key : undefined;
}

/** Reads value from an item that may be CustomQueryStringParameterItems or REST-adapted shape. */
function getParamValue(item: Record<string, unknown>): string | undefined {
  const value = item.value ?? item.paramValue;
  return value !== undefined && value !== null ? String(value) : undefined;
}

export const convertCustomQspsToObject = (
  collectionItems: CustomQueryStringParameterItems[],
): Record<string, string> => {
  if (!collectionItems || collectionItems.length === 0) {
    return {};
  }

  return collectionItems.reduce(
    (acc, item) => {
      const raw = item as unknown as Record<string, unknown>;
      const key = getParamKey(raw);
      const value = getParamValue(raw);
      if (key !== undefined && key !== '') {
        acc[key] = value ?? '';
      }
      return acc;
    },
    {} as Record<string, string>,
  );
};

/**
 * Returns the letter label for a given index (A, B, ... Z, AA, AB, ...)
 */
export const getLetterLabel = (index: number): string => {
  let label = '';
  let i = index;
  do {
    label = String.fromCharCode(65 + (i % 26)) + label;
    i = Math.floor(i / 26) - 1;
  } while (i >= 0);
  return label;
};

export const slugify = (str: string) => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const unslugify = (slug: string) => {
  let title = slug.replace(/-/g, ' ');
  title = title.replace(/\b[a-z]/g, function (match) {
    return match.toUpperCase();
  });

  return title;
};
